/**
 * Itinerary PE / fare-upgrade helpers.
 *
 * Mirrors the CTFareChangeCallout flow in FareFamilyFareBlock:
 * - resolve current selected fare(s)
 * - resolve the cheapest PREMIUM_ECONOMY fare available per leg
 * - build callout comparison props
 * - on confirm, SELECT the new fare card(s) via the same update payload
 */

import { ravenSDKItinTrigger } from "@/analytics/raven/ravenSDKHelper"
import { updateItineraryDataCFW } from "@/api/itinerary/cfw"
import type { CabinUpgradeCallout, UpgradeCallout } from "@/types/PremiumEconomyApi"
import { findCabinClassSummary, resolveItineraryUpgradeOffer } from "@/utils/flights/pe/media"

import { serializeItineraryData } from "."

export const normalizeCabinType = (cabinType?: string): string => (cabinType || '').trim().toUpperCase().replace(/\s+/g, '_')
export type FareCardLike = {
  fareId?: string
  fareCardType?: string
  selected?: boolean
  totalFareAmount?: number
  cabinType?: string
  displayCabinType?: string
  displayCabinClass?: string
  fareCategory?: string
  fareCardMessage?: {
    title?: string
    subTitle?: string
  }
  isDisabled?: boolean
  [key: string]: unknown
}

export type FareCardsDataLike = {
  fareCards?: FareCardLike[]
  onwardFareCards?: FareCardLike[]
  returnFareCards?: FareCardLike[]
  tripFareCards?: FareCardLike[]
  onwardCabinClassSummary?: unknown
  returnCabinClassSummary?: unknown
  tripCabinClassSummary?: unknown
  CABIN_UPGRADE_CALLOUT?: unknown
  cabinUpgradeCallout?: unknown
}

export type FareRouteInfo = {
  originCode: string
  destinationCode: string
  ctFareName: string
  ctFareAmount: number
  newFareName: string
  newFareAmount: number
}

export type PremiumEconomyFareUpgrade = {
  /** True when at least one leg has a cheaper PE fare that is not already selected. */
  canUpgrade: boolean
  isRoundTrip: boolean
  isSpecialRt: boolean
  selectedOnwardFare: FareCardLike | null
  selectedReturnFare: FareCardLike | null
  cheapestPeOnwardFare: FareCardLike | null
  cheapestPeReturnFare: FareCardLike | null
  /** Fare cards to SELECT on confirm (1 for OW/special RT, 2 for normal RT). */
  targetFareCards: FareCardLike[]
  onwardFareInfo: FareRouteInfo | null
  returnFareInfo: FareRouteInfo | null
  /** Sum of target fare amounts — used as PREMIUM_ECONOMY footer total when breakup is unavailable. */
  totalAmount: number
}

type FlightLegCodes = {
  originCode?: string
  destinationCode?: string
}

type BuildUpgradeOptions = {
  journeyType?: string
  onwardCodes?: FlightLegCodes
  returnCodes?: FlightLegCodes
  searchedFromAirport?: string
  searchedToAirport?: string
  /** From CABIN_UPGRADE_CALLOUT.targetCabin — may be INDIGO_UPFRONT. */
  targetCabin?: string | null
}

const fareCardRef = (fare: FareCardLike) => ({
  fareCardType: fare?.fareCardType,
  fareId: fare?.fareId,
})

const getFareAmount = (fare?: FareCardLike | null) => Number(fare?.totalFareAmount) || 0

const getFareName = (fare?: FareCardLike | null, fallback = '') =>
  fare?.fareCardMessage?.title || fallback

const getSelectedFareCard = (fareCards: FareCardLike[] = []): FareCardLike | null =>
  fareCards.find((fareCard) => fareCard?.selected) || null

/** Normalise cabin from fare card (`cabinType`, with `fareCategory` fallback for unmigrated payloads). */
export const getFareCabinType = (fare?: FareCardLike | null): string =>
  normalizeCabinType(String(fare?.cabinType || fare?.fareCategory || ''))

const getFareDisplayCabin = (fare?: FareCardLike | null): string =>
  normalizeCabinType(String(fare?.displayCabinType || fare?.displayCabinClass || '')) ||
  getFareCabinType(fare)

export const isPremiumEconomyFareCard = (fare?: FareCardLike | null): boolean =>
  getFareCabinType(fare) === 'PREMIUM_ECONOMY'

/** Cheapest enabled fare for a display-cabin tab (INDIGO_UPFRONT / PREMIUM_ECONOMY / …). */
export const getCheapestFareCardByDisplayCabin = (
  fareCards: FareCardLike[] = [],
  displayCabin?: string | null
): FareCardLike | null => {
  const cabin = normalizeCabinType(String(displayCabin || ''))
  if (!cabin || !Array.isArray(fareCards) || fareCards.length === 0) return null

  const matching = fareCards.filter(
    (f) => f && !f.isDisabled && getFareDisplayCabin(f) === cabin
  )
  if (matching.length === 0) return null

  return matching.reduce<FareCardLike | null>((cheapest, fare) => {
    if (!cheapest) return fare
    const cheapestAmount = getFareAmount(cheapest)
    const fareAmount = getFareAmount(fare)
    if (fareAmount < cheapestAmount) return fare
    if (fareAmount === cheapestAmount && !fare.selected && cheapest.selected) return fare
    return cheapest
  }, null)
}

/** Cheapest enabled `PREMIUM_ECONOMY` fare by `totalFareAmount`; prefers non-selected on ties. */
export const getCheapestPremiumEconomyFareCard = (
  fareCards: FareCardLike[] = []
): FareCardLike | null => getCheapestFareCardByDisplayCabin(fareCards, 'PREMIUM_ECONOMY') ||
  // Theme-cabin fallback when displayCabinType is absent on PE fares.
  (() => {
    if (!Array.isArray(fareCards) || fareCards.length === 0) return null
    const peFares = fareCards.filter((f) => f && !f.isDisabled && isPremiumEconomyFareCard(f))
    if (peFares.length === 0) return null
    return peFares.reduce<FareCardLike | null>((cheapest, fare) => {
      if (!cheapest) return fare
      const cheapestAmount = getFareAmount(cheapest)
      const fareAmount = getFareAmount(fare)
      if (fareAmount < cheapestAmount) return fare
      if (fareAmount === cheapestAmount && !fare.selected && cheapest.selected) return fare
      return cheapest
    }, null)
  })()

const resolveTargetFareCard = (
  fareCards: FareCardLike[],
  targetCabin?: string | null
): FareCardLike | null => {
  const cabin = normalizeCabinType(String(targetCabin || 'PREMIUM_ECONOMY'))
  return (
    getCheapestFareCardByDisplayCabin(fareCards, cabin) ||
    (cabin === 'PREMIUM_ECONOMY' ? getCheapestPremiumEconomyFareCard(fareCards) : null)
  )
}

const buildFareRouteInfo = (
  selectedFare: FareCardLike | null,
  targetFare: FareCardLike | null,
  codes: FlightLegCodes = {},
  fallbackOrigin = '',
  fallbackDestination = ''
): FareRouteInfo => ({
  originCode: codes.originCode || fallbackOrigin,
  destinationCode: codes.destinationCode || fallbackDestination,
  ctFareName: getFareName(selectedFare, 'Current'),
  ctFareAmount: getFareAmount(selectedFare),
  newFareName: getFareName(targetFare, ''),
  newFareAmount: getFareAmount(targetFare),
})

/**
 * Resolves selected vs cheapest PREMIUM_ECONOMY fares for the current itinerary.
 * Round-trip (non special-RT) upgrades BOTH legs to their cheapest PE fare.
 */
export const getCheapestPremiumEconomyFareUpgrade = (
  fareCardsData: FareCardsDataLike = {},
  options: BuildUpgradeOptions = {}
): PremiumEconomyFareUpgrade => {
  const {
    journeyType = 'ONE_WAY',
    onwardCodes = {},
    returnCodes = {},
    searchedFromAirport = '',
    searchedToAirport = '',
    targetCabin = 'PREMIUM_ECONOMY',
  } = options

  const isRoundTrip = journeyType !== 'ONE_WAY'
  const isSpecialRt = !!(fareCardsData?.tripFareCards && fareCardsData.tripFareCards.length > 0)

  const empty: PremiumEconomyFareUpgrade = {
    canUpgrade: false,
    isRoundTrip,
    isSpecialRt,
    selectedOnwardFare: null,
    selectedReturnFare: null,
    cheapestPeOnwardFare: null,
    cheapestPeReturnFare: null,
    targetFareCards: [],
    onwardFareInfo: null,
    returnFareInfo: null,
    totalAmount: 0,
  }

  if (!fareCardsData || Object.keys(fareCardsData).length === 0) return empty

  // One-way or special RT — single fare list
  if (!isRoundTrip || isSpecialRt) {
    const fareCards = isSpecialRt
      ? fareCardsData.tripFareCards || []
      : fareCardsData.fareCards || fareCardsData.onwardFareCards || []
    const selected = getSelectedFareCard(fareCards) || null
    const cheapestPe = resolveTargetFareCard(fareCards, targetCabin)
    if (!cheapestPe) return empty

    const canUpgrade = cheapestPe.fareId !== selected?.fareId
    const onwardFareInfo = buildFareRouteInfo(
      selected,
      cheapestPe,
      onwardCodes,
      searchedFromAirport,
      searchedToAirport
    )

    return {
      canUpgrade,
      isRoundTrip,
      isSpecialRt,
      selectedOnwardFare: selected,
      selectedReturnFare: null,
      cheapestPeOnwardFare: cheapestPe,
      cheapestPeReturnFare: null,
      targetFareCards: [cheapestPe],
      onwardFareInfo,
      returnFareInfo: null,
      totalAmount: getFareAmount(cheapestPe),
    }
  }

  // Normal round-trip — upgrade both legs to cheapest PE
  const onwardFareCards = fareCardsData.onwardFareCards || []
  const returnFareCards = fareCardsData.returnFareCards || []
  const selectedOnward = getSelectedFareCard(onwardFareCards) || null
  const selectedReturn = getSelectedFareCard(returnFareCards) || null
  const cheapestPeOnward = resolveTargetFareCard(onwardFareCards, targetCabin)
  const cheapestPeReturn = resolveTargetFareCard(returnFareCards, targetCabin)

  if (!cheapestPeOnward || !cheapestPeReturn) return empty

  const canUpgrade =
    cheapestPeOnward.fareId !== selectedOnward?.fareId ||
    cheapestPeReturn.fareId !== selectedReturn?.fareId

  const onwardFareInfo = buildFareRouteInfo(
    selectedOnward,
    cheapestPeOnward,
    onwardCodes,
    searchedFromAirport,
    searchedToAirport
  )
  const returnFareInfo = buildFareRouteInfo(
    selectedReturn,
    cheapestPeReturn,
    returnCodes,
    searchedToAirport,
    searchedFromAirport
  )

  return {
    canUpgrade: Boolean(canUpgrade),
    isRoundTrip,
    isSpecialRt,
    selectedOnwardFare: selectedOnward,
    selectedReturnFare: selectedReturn,
    cheapestPeOnwardFare: cheapestPeOnward,
    cheapestPeReturnFare: cheapestPeReturn,
    targetFareCards: [cheapestPeOnward, cheapestPeReturn],
    onwardFareInfo,
    returnFareInfo,
    totalAmount: getFareAmount(cheapestPeOnward) + getFareAmount(cheapestPeReturn),
  }
}

/** Same SELECT / FARE_CARD payload shape as FareFamilyFareBlock.executeFareSelection. */
export const buildPremiumEconomyFareSelectPayload = (targetFareCards: FareCardLike[]) => ({
  updateRequestList: [
    {
      operationType: 'SELECT',
      type: 'FARE_CARD',
      timestamp: new Date().getTime(),
      selectedFareCards: targetFareCards.map(fareCardRef),
    },
  ],
})

type ConfirmOptions = {
  itineraryId: string
  isCfwUser?: boolean
  /** Pre-update itinerary snapshot — same payload source as FareFamilyFareBlock.executeFareSelection. */
  itineraryData?: any
  onSuccess?: (serializedData: Record<string, unknown>) => void
  onFailure?: (error?: unknown) => void
}

/**
 * Confirms the PE upgrade the same way FareFamilyFareBlock.handleCTFareChangeConfirm does:
 * SELECT the target fare card(s), then serialize + hand off the new itinerary components.
 */
export const confirmPremiumEconomyFareSelection = async (
  targetFareCards: FareCardLike[],
  { itineraryId, isCfwUser = false, itineraryData, onSuccess, onFailure }: ConfirmOptions
) => {
  if (!itineraryId || !targetFareCards?.length) {
    onFailure?.(new Error('Missing itineraryId or target fare cards'))
    return { success: false, serializedData: null as Record<string, unknown> | null }
  }

  try {
    const payload = buildPremiumEconomyFareSelectPayload(targetFareCards)
    const response = await updateItineraryDataCFW(payload, itineraryId, isCfwUser)
     // @ts-ignore
    const { responseCode, data: responseData = {} } = response || {}
    const success = responseCode === 200 && !!responseData?.components

    if (success) {
      const serializedData = serializeItineraryData(responseData.components)
      const primaryFare = targetFareCards[0]
      ravenSDKItinTrigger('a_ui_action', itineraryData, {
        action_name: 'cfw_fare_selected',
        action_type: 'click',
        page_name: 'flights_itinerary',
        is_cfw_user: isCfwUser,
        a_cfw_fare_type: primaryFare?.fareCardMessage?.subTitle,
      })
      onSuccess?.(serializedData)
      return { success: true, serializedData }
    }

    onFailure?.()
    return { success: false, serializedData: null }
  } catch (error) {
    console.error(error)
    onFailure?.(error)
    return { success: false, serializedData: null }
  }
}

const IU_ITINERARY_CALLOUT_STORAGE = 'iu_itinerary_callout'

const hasItineraryCalloutLegs = (payload?: CabinUpgradeCallout | null): boolean => {
  const map = payload?.upgradeCallout
  return !!(map?.tripUpgradeCallout || map?.onwardUpgradeCallout || map?.returnUpgradeCallout)
}

/** Carry the interstitial IU callout onto itinerary when BE omits CABIN_UPGRADE_CALLOUT. */
export const persistIuItineraryCallout = (callout?: UpgradeCallout | null): void => {
  if (typeof window === 'undefined' || !callout?.title || !callout.targetCabin) return
  try {
    sessionStorage.setItem(
      IU_ITINERARY_CALLOUT_STORAGE,
      JSON.stringify({ upgradeCallout: { tripUpgradeCallout: callout } })
    )
  } catch {
    /* ignore */
  }
}

const readPersistedIuItineraryCallout = (): CabinUpgradeCallout | null => {
  if (typeof window === 'undefined') return null
  try {
    const raw = sessionStorage.getItem(IU_ITINERARY_CALLOUT_STORAGE)
    if (!raw) return null
    const parsed = JSON.parse(raw) as CabinUpgradeCallout
    return hasItineraryCalloutLegs(parsed) ? parsed : null
  } catch {
    return null
  }
}

/** CABIN_UPGRADE_CALLOUT component, or the same callout nested on an Economy fare card. */
export const getItineraryUpgradeCalloutPayload = (
  itineraryData: any
): CabinUpgradeCallout | null => {
  const fareCardsData: FareCardsDataLike = itineraryData?.FARE_CARDS || {}
  const directCandidates = [
    itineraryData?.CABIN_UPGRADE_CALLOUT,
    itineraryData?.cabinUpgradeCallout,
    fareCardsData.CABIN_UPGRADE_CALLOUT,
    fareCardsData.cabinUpgradeCallout,
  ]
  for (const candidate of directCandidates) {
    if (hasItineraryCalloutLegs(candidate as CabinUpgradeCallout)) {
      return candidate as CabinUpgradeCallout
    }
  }

  const lists = [
    fareCardsData.fareCards,
    fareCardsData.onwardFareCards,
    fareCardsData.returnFareCards,
    fareCardsData.tripFareCards,
  ]
  for (const list of lists) {
    if (!Array.isArray(list)) continue
    for (const fare of list) {
      if (fare?.upgradeCallout) {
        return {
          upgradeCallout: { tripUpgradeCallout: fare.upgradeCallout as UpgradeCallout },
        }
      }
    }
  }
  return readPersistedIuItineraryCallout()
}

/** Convenience: derive PE upgrade from itineraryData + flight codes for CTFareChangeCallout. */
export const getCheapestPremiumEconomyFareUpgradeFromItinerary = (
  itineraryData: any
): PremiumEconomyFareUpgrade => {
  const searchDetails = itineraryData?.SEARCH_DETAILS || {}
  const flights = itineraryData?.FLIGHT?.flights || []
  const onwardFlight = flights?.[0] || {}
  const returnFlight = flights?.[1] || {}
  const cabinSummary = findCabinClassSummary(itineraryData)
  const upgradeOffer = resolveItineraryUpgradeOffer(
    getItineraryUpgradeCalloutPayload(itineraryData),
    'trip',
    cabinSummary
  )
  const targetCabin = upgradeOffer.targetCabin || 'PREMIUM_ECONOMY'

  return getCheapestPremiumEconomyFareUpgrade(itineraryData?.FARE_CARDS || {}, {
    journeyType: searchDetails?.journeyType,
    searchedFromAirport: searchDetails?.searchedFromAirport || '',
    searchedToAirport: searchDetails?.searchedToAirport || '',
    targetCabin,
    onwardCodes: {
      originCode: onwardFlight?.departureAirportCode,
      destinationCode: onwardFlight?.arrivalAirportCode,
    },
    returnCodes: {
      originCode: returnFlight?.departureAirportCode,
      destinationCode: returnFlight?.arrivalAirportCode,
    },
  })
}

/**
 * Resolves the cabin currently selected on the itinerary for Redux `selectedCabinType`.
 * Prefers the selected fare card's `cabinType` (special-RT trip card → onward → return),
 * then falls back to `SEARCH_DETAILS.cabinType`.
 */
export const getSelectedCabinTypeFromItinerary = (itineraryData: any): string => {
  const fareCardsData: FareCardsDataLike = itineraryData?.FARE_CARDS || {}
  const searchCabinType = normalizeCabinType(
    String(itineraryData?.SEARCH_DETAILS?.cabinType || '')
  )

  const tripFareCards = fareCardsData.tripFareCards || []
  if (tripFareCards.length > 0) {
    const selectedTrip = getSelectedFareCard(tripFareCards)
    const cabin = getFareCabinType(selectedTrip)
    return cabin || searchCabinType
  }

  const selectedOnward =
    getSelectedFareCard(fareCardsData.onwardFareCards || fareCardsData.fareCards || []) || null
  const selectedReturn = getSelectedFareCard(fareCardsData.returnFareCards || []) || null

  const onwardCabin = getFareCabinType(selectedOnward)
  if (onwardCabin) return onwardCabin

  const returnCabin = getFareCabinType(selectedReturn)
  if (returnCabin) return returnCabin

  return searchCabinType
}
